import type { IconType } from 'src/components/Shared/Icon/types';
import type { PickerOptions } from 'src/components/Shared/PickerSelect/TimeDurationPickerField';
import * as yup from 'yup';

export const baseCloseWorkOrderSchema = {
  elapse_time: yup.object().shape({
    travel_time_in_sec: yup.number().required(),
    completed_time_in_sec: yup.number().required(),
  }),
  labor_in_cent: yup.number().integer('Labor cost must be an integer').optional(),
  exceed_nte_reason: yup.string().optional().nullable(),
  additional_info: yup.object().shape({
    state: yup.string().optional(),
    additional: yup.boolean().optional(),
    description: yup.string().optional(),
  }),
  time_adjusted_reason: yup.string(),
  reason: yup.string(),
  service_notes: yup.string().optional(),
  materialsToDelete: yup.array().of(yup.string()).optional(),
};

export const closeWithQuotesSchema = yup.object().shape({ ...baseCloseWorkOrderSchema });

export const closeWithoutQuotesSchema = yup.object().shape({
  ...baseCloseWorkOrderSchema,
  issues: yup
    .array()
    .of(
      yup.object().shape({
        issue_id: yup.string().required(),
        title: yup.string().required(),
        status: yup.object().shape({
          label: yup.string().required(),
          value: yup.string().required(),
        }),
        labor_in_cent: yup.number().integer('Labor cost must be an integer').optional(),
        materials: yup
          .array()
          .of(
            yup.object().shape({
              label: yup.string().required(),
              markup_fee_type: yup.string().required(),
              markup_fee_type_value: yup.number().required(),
              markup_fee_in_cents: yup.number().required(),
              cost_in_cents: yup.number().nullable().default(null),
              total_cost_in_cents: yup.number().required(),
              quantity: yup.number().required(),
              quantity_type: yup.string().required(),
              cost_type: yup.string().oneOf(['total', 'per-unit']).required(),
              unit_price_in_cents: yup.number().nullable().default(null),
              material_id: yup.string().optional(),
              updated: yup.boolean().optional(),
              new: yup.boolean().optional(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
});

export type FormState =
  | yup.InferType<typeof closeWithQuotesSchema>
  | yup.InferType<typeof closeWithoutQuotesSchema>;

export type WOCloseWorkOrderHomeState =
  | 'good-condition'
  | 'slightly-neglected'
  | 'severely-neglected';

export const HOME_STATE_ICON_MAP: Record<WOCloseWorkOrderHomeState, IconType> = {
  'good-condition': 'home-smile',
  'slightly-neglected': 'home-line',
  'severely-neglected': 'home-sad',
};

export const WO_CLOSE_WORK_ORDER_TABS_KEYS = {
  SUMMARY: 'summary',
  ADDITIONAL_INFO: 'additional_info',
  CLOSED: 'closed',
  QUOTE_DETAILS: 'quote_details',
} as const;

export type WOCloseWorkOrderTabs =
  (typeof WO_CLOSE_WORK_ORDER_TABS_KEYS)[keyof typeof WO_CLOSE_WORK_ORDER_TABS_KEYS];

export type TabData = {
  TITLE?: string;
  HIDE_HEADER?: boolean;
  HIDE_FOOTER?: boolean;
} & ({ HIDE_SUBMIT: true } | { HIDE_SUBMIT?: false; SUBMIT_LABEL: string }) &
  ({ HIDE_BACK: true } | { HIDE_BACK?: false; BACK_LABEL: string; BACK_STYLE: string });

export const WO_CLOSE_WORK_ORDER_TAB_DATA: Record<WOCloseWorkOrderTabs, TabData> = {
  [WO_CLOSE_WORK_ORDER_TABS_KEYS.SUMMARY]: {
    TITLE: 'Review Summary',
    SUBMIT_LABEL: 'Next',
    HIDE_BACK: true,
  },
  [WO_CLOSE_WORK_ORDER_TABS_KEYS.ADDITIONAL_INFO]: {
    TITLE: 'Additional Info',
    SUBMIT_LABEL: 'Close Work Order',
    HIDE_BACK: true,
  },
  [WO_CLOSE_WORK_ORDER_TABS_KEYS.CLOSED]: {
    BACK_LABEL: 'Return to Details',
    BACK_STYLE: 'has-secondary',
    HIDE_HEADER: true,
    HIDE_SUBMIT: true,
  },
  [WO_CLOSE_WORK_ORDER_TABS_KEYS.QUOTE_DETAILS]: {
    TITLE: 'Quote Details',
    SUBMIT_LABEL: 'Next',
    HIDE_BACK: true,
  },
};

export const WO_CLOSE_WORK_ORDER_ADJUST_ELAPSED_TIME = {
  TITLE: 'Adjust Elapsed Time',
} as const;

export const MINUTE_LOOKUP = [0, 15, 30, 45];

export const HOUR_LOOKUP: PickerOptions = {
  0: MINUTE_LOOKUP,
  1: MINUTE_LOOKUP,
  2: MINUTE_LOOKUP,
  3: MINUTE_LOOKUP,
  4: MINUTE_LOOKUP,
  5: MINUTE_LOOKUP,
  6: MINUTE_LOOKUP,
  7: MINUTE_LOOKUP,
  8: MINUTE_LOOKUP,
};
